/**
 * Autodesk OAuth context: AuthProvider + useAutodeskAuth().
 * Centralizes Autodesk token state; no Datum360 logic.
 */
import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  getStoredTokens,
  hasValidAccessToken,
  loginWithAutodesk as serviceLogin,
  refreshAccessToken,
  clearAuth,
} from "./authService";

const AutodeskAuthContext = createContext(null);

export function AutodeskAuthProvider({ children }) {
  const navigate = useNavigate();
  const [, setRefresh] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const refreshAndUpdate = useCallback(async () => {
    try {
      await refreshAccessToken();
      setError(null);
      setRefresh((n) => n + 1);
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Refresh failed");
      clearAuth();
      setRefresh((n) => n + 1);
      return false;
    }
  }, []);

  // Hydrate from sessionStorage and optionally refresh if near expiry
  useEffect(() => {
    let cancelled = false;
    const init = async () => {
      const current = getStoredTokens();
      if (current && !hasValidAccessToken()) {
        if (current.refresh_token) {
          const ok = await refreshAndUpdate();
          if (cancelled) return;
          if (!ok) {
            serviceLogin();
            return;
          }
        } else {
          clearAuth();
          setRefresh((n) => n + 1);
          serviceLogin();
          return;
        }
      }
      if (!cancelled) setIsLoading(false);
    };
    init();
    return () => { cancelled = true; };
  }, [refreshAndUpdate]);

  const loginWithAutodesk = useCallback(() => {
    setError(null);
    serviceLogin();
  }, []);

  const logout = useCallback(() => {
    clearAuth();
    setError(null);
    setRefresh((n) => n + 1);
    navigate("/", { replace: true });
  }, [navigate]);

  // Derive from AuthService so callback-updated tokens are visible after navigate
  const tokens = getStoredTokens();
  const isAuthenticated = Boolean(
    tokens?.access_token && (hasValidAccessToken() || tokens?.refresh_token)
  );

  const value = {
    isAuthenticated,
    isLoading,
    loginWithAutodesk,
    logout,
    tokens,
    error,
    refreshAndUpdate,
    hasValidAccessToken,
  };

  return (
    <AutodeskAuthContext.Provider value={value}>
      {children}
    </AutodeskAuthContext.Provider>
  );
}

export function useAutodeskAuth() {
  const ctx = useContext(AutodeskAuthContext);
  if (ctx == null) {
    throw new Error("useAutodeskAuth must be used within AutodeskAuthProvider");
  }
  return ctx;
}
